import React from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useFormikContext } from "formik";

import usCities from "@/data/us-cities.json";
import usStates from "@/data/us-states.json";
import usRegions from "@/data/us-regions.json";
import departments from "@/data/departments.json";
import functions from "@/data/functions.json";
import industries from "@/data/industries.json";

function Section({ title, children, defaultExpanded = false }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} elevation={1} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2}>
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool
};

function ReadOnlyField({ label, value }) {
  return (
    <TextField
      label={label}
      value={value ?? "—"}
      InputProps={{ readOnly: true }}
      size="small"
      fullWidth
      variant="filled"
    />
  );
}

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default function CandidateProfileForm({ bullhorn }) {
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const bh = bullhorn ?? {};
  const fullName = [bh.firstName, bh.lastName].filter(Boolean).join(" ") || "—";
  const currentLocation =
    bh.currentCity && bh.currentState
      ? `${bh.currentCity}, ${bh.currentState}`
      : bh.currentState ?? "—";

  return (
    <Box>
      {/* 1. Bullhorn read-only */}
      <Section title="Bullhorn Info (read-only)" defaultExpanded>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <ReadOnlyField label="Name" value={fullName} />
          <ReadOnlyField label="Email" value={bh.email} />
          <ReadOnlyField label="Current Location" value={currentLocation} />
          <ReadOnlyField label="Current Title / Occupation" value={bh.occupation} />
          <ReadOnlyField label="Undergrad Year" value={bh.undergradYear} />
          <ReadOnlyField label="Undergrad School" value={bh.undergradSchool} />
          <ReadOnlyField label="Branded Company" value={bh.brandedCompany} />
        </Box>
      </Section>

      {/* 2. Variner flags */}
      <Section title="Variner Flags">
        <FormControlLabel
          control={
            <Switch
              checked={values.high_potential ?? false}
              onChange={(e) => setFieldValue("high_potential", e.target.checked)}
              color="warning"
            />
          }
          label="High Potential"
        />
        <FormControlLabel
          control={
            <Switch
              checked={values.hiring_manager ?? false}
              onChange={(e) => setFieldValue("hiring_manager", e.target.checked)}
              color="secondary"
            />
          }
          label="Hiring Manager"
        />
      </Section>

      {/* 3. Location preferences */}
      <Section title="Location Preferences">
        <Autocomplete
          multiple
          options={usCities}
          getOptionLabel={(o) => `${o.city}, ${o.state}`}
          isOptionEqualToValue={(o, v) => o.city === v.city && o.state === v.state}
          value={values.desired_cities ?? []}
          onChange={(_, v) => setFieldValue("desired_cities", v)}
          renderTags={(val, getTagProps) =>
            val.map((o, i) => (
              <Chip
                key={i}
                label={`${o.city}, ${o.state}`}
                size="small"
                {...getTagProps({ index: i })}
              />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Desired Cities" size="small" />}
        />
        <Autocomplete
          multiple
          options={usStates}
          value={values.desired_states ?? []}
          onChange={(_, v) => setFieldValue("desired_states", v)}
          renderTags={(val, getTagProps) =>
            val.map((s, i) => (
              <Chip key={i} label={s} size="small" {...getTagProps({ index: i })} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Desired States" size="small" />}
        />
        <Autocomplete
          multiple
          options={usRegions}
          value={values.desired_regions ?? []}
          onChange={(_, v) => setFieldValue("desired_regions", v)}
          renderTags={(val, getTagProps) =>
            val.map((r, i) => (
              <Chip key={i} label={r} size="small" {...getTagProps({ index: i })} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Desired Regions" size="small" />}
        />
      </Section>

      {/* 4. Work type */}
      <Section title="Work Type">
        <Autocomplete
          multiple
          options={departments}
          value={values.desired_departments ?? []}
          onChange={(_, v) => setFieldValue("desired_departments", v)}
          renderTags={(val, getTagProps) =>
            val.map((d, i) => (
              <Chip key={i} label={d} size="small" {...getTagProps({ index: i })} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Desired Departments" size="small" />
          )}
        />
        <Autocomplete
          multiple
          options={functions}
          value={values.desired_functions ?? []}
          onChange={(_, v) => setFieldValue("desired_functions", v)}
          renderTags={(val, getTagProps) =>
            val.map((f, i) => (
              <Chip key={i} label={f} size="small" {...getTagProps({ index: i })} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Desired Functions" size="small" />}
        />
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={values.desired_categories ?? []}
          onChange={(_, v) => setFieldValue("desired_categories", v)}
          renderTags={(val, getTagProps) =>
            val.map((c, i) => (
              <Chip key={i} label={c} size="small" {...getTagProps({ index: i })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Desired Categories (Variner)"
              size="small"
              helperText="Press Enter to add a custom value"
            />
          )}
        />
      </Section>

      {/* 5. Industry */}
      <Section title="Industry">
        <Autocomplete
          multiple
          options={industries}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.variner_value === v.variner_value}
          value={values.desired_industries ?? []}
          onChange={(_, v) => setFieldValue("desired_industries", v)}
          renderTags={(val, getTagProps) =>
            val.map((ind, i) => (
              <Chip key={i} label={ind.label} size="small" {...getTagProps({ index: i })} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Desired Industries" size="small" />
          )}
        />
      </Section>

      {/* 6. Compensation */}
      <Section title="Compensation">
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <TextField
            label="Desired Base Salary"
            type="number"
            size="small"
            value={values.desired_base_salary ?? ""}
            onChange={(e) =>
              setFieldValue(
                "desired_base_salary",
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
            inputProps={{ min: 0 }}
            error={touched.desired_base_salary && Boolean(errors.desired_base_salary)}
            helperText={touched.desired_base_salary && errors.desired_base_salary}
          />
          <TextField
            label="Desired Total Compensation"
            type="number"
            size="small"
            value={values.desired_total_compensation ?? ""}
            onChange={(e) =>
              setFieldValue(
                "desired_total_compensation",
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
            inputProps={{ min: 0 }}
            error={touched.desired_total_compensation && Boolean(errors.desired_total_compensation)}
            helperText={touched.desired_total_compensation && errors.desired_total_compensation}
          />
        </Box>
        {values.compensation_date && (
          <Typography variant="caption" color="text.secondary">
            Compensation last updated:{" "}
            {new Date(values.compensation_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric"
            })}
          </Typography>
        )}
      </Section>

      {/* 7. Title */}
      <Section title="Desired Title">
        <TextField
          label="Desired Title"
          size="small"
          fullWidth
          value={values.desired_title ?? ""}
          onChange={(e) => setFieldValue("desired_title", e.target.value)}
          inputProps={{ maxLength: 200 }}
          error={touched.desired_title && Boolean(errors.desired_title)}
          helperText={touched.desired_title && errors.desired_title}
        />
        <TextField
          label="Desired Title (Variner value)"
          size="small"
          fullWidth
          value={values.desired_title_variner_value ?? ""}
          onChange={(e) => setFieldValue("desired_title_variner_value", e.target.value)}
          inputProps={{ maxLength: 200 }}
        />
      </Section>

      {/* 8. Remote */}
      <Section title="Remote">
        <FormControlLabel
          control={
            <Switch
              checked={values.open_to_remote ?? false}
              onChange={(e) => setFieldValue("open_to_remote", e.target.checked)}
              color="primary"
            />
          }
          label="Open to Remote"
        />
      </Section>
    </Box>
  );
}

CandidateProfileForm.propTypes = {
  bullhorn: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    occupation: PropTypes.string,
    currentCity: PropTypes.string,
    currentState: PropTypes.string,
    undergradYear: PropTypes.number,
    undergradSchool: PropTypes.string,
    brandedCompany: PropTypes.string
  })
};
